import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { HistoriaNaMesa } from '@/historias/mesa';
import { useSessao } from '@/modulos/sessao/consultas';
import { UtilidadeDaResposta } from '@/modulos/tecnico/UtilidadeDaResposta';

import { explicarFalha, type usePedirHipotese } from './consultas';

/**
 * IA 03 — pedir a hipótese de diagnóstico a partir do sintoma. Só aparece para
 * quem executa a O.S. e pode perguntar ao assistente (cada pedido tem custo).
 * A resposta sem base nos documentos não vai para a O.S.: a tela diz o que faltou.
 */
export function PedirHipotese({
  ordemId,
  jaTem,
  pedir,
}: {
  readonly ordemId: string;
  readonly jaTem: boolean;
  /** O pedido é de quem mostra a hipótese: é lá que o lugar dela vira esqueleto enquanto a IA consulta (p.32). */
  readonly pedir: ReturnType<typeof usePedirHipotese>;
}) {
  const sessao = useSessao();
  const pode = (sessao.data?.permissoes ?? []).includes('ia.perguntar');
  const [recado, setRecado] = useState<string | null>(null);
  /* A hipótese recém-pedida, para a pessoa dizer se serviu (0076). */
  const [registro, setRegistro] = useState<string | null>(null);
  if (!pode) return null;

  return (
    <div className="flex flex-col gap-2">
      {/* A frase desce para baixo do botão quando o painel aperta (R01,
          p.28), em vez de virar uma tira de quatro linhas ao lado dele. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <HistoriaNaMesa codigo="BTN-OSV-04" className="flex">
          <Botao
            nivel="consulta"
            disabled={pedir.isPending}
            onClick={() => {
              setRecado(null);
              setRegistro(null);
              pedir.mutate(ordemId, {
                onSuccess: (r) => {
                  setRecado(r.gravadaNaOrdem ? null : `Sem hipótese: ${r.falta ?? 'os documentos da máquina não tratam deste sintoma'}.`);
                  setRegistro(r.id ?? null);
                },
                onError: (falha) => {
                  const f = explicarFalha(falha);
                  setRecado(`${f.erro}. ${f.comoResolver}`);
                },
              });
            }}
          >
            {pedir.isPending ? 'Consultando os documentos…' : jaTem ? 'Pedir outra hipótese' : 'Pedir hipótese'}
          </Botao>
        </HistoriaNaMesa>
        {/* Rótulo em verbo + objeto, até três palavras (p.15): o azul de consulta
            já diz que é a IA, e a frase ao lado também. */}
        <span className="txt-apoio text-tinta-apagada">A IA cita o manual da máquina. Cada pedido tem custo.</span>
      </div>
      {recado ? (
        <p role="status" className="txt-apoio text-tinta-secundaria">
          {recado}
        </p>
      ) : null}
      {registro && !pedir.isPending ? <UtilidadeDaResposta key={registro} perguntaId={registro} /> : null}
    </div>
  );
}
